import type { iOnboardingFlags } from "../../../../types/internal";
import type { iOnboardingFlagsAPI } from "../useOnBoarding";

type OnboardingProfileInput =
  | Partial<iOnboardingFlagsAPI>
  | Partial<iOnboardingFlags>
  | null
  | undefined;

/**
 * Dashboard onboarding wizard display positions map to these API
 * `onboarding_step_*` indices (Reviews → Popups → Cart → Chats → Wishlist → Domain).
 * API step 7 (Send a test email) is intentionally omitted from the wizard/checklist.
 */
export const ONBOARDING_WIZARD_API_SEQUENCE: readonly number[] = [
  2, 3, 5, 1, 4, 6,
  // 7, // Send a test email — removed from checklist/wizard; flag still auto-set on complete for backend compat
];

/** Normalizes API/DB flag values (bool, 0/1, "0"/"1", "true"). */
export function isProfileFlagComplete(value: unknown): boolean {
  if (value === true || value === 1) {
    return true;
  }
  if (value === false || value === 0 || value === null || value === undefined) {
    return false;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "1" || normalized === "true" || normalized === "yes") {
      return true;
    }
    if (normalized === "0" || normalized === "false" || normalized === "no" || normalized === "") {
      return false;
    }
  }

  return Boolean(value);
}

export function isOnboardingApiStepComplete(
  parsed: OnboardingProfileInput,
  apiStep: number
): boolean {
  if (!parsed) return false;
  if (apiStep === 3) {
    const v = parsed.onboarding_step_3;
    return v != null && Number(v) >= 5;
  }
  const key = `onboarding_step_${apiStep}` as keyof iOnboardingFlagsAPI;
  return isProfileFlagComplete(parsed[key]);
}

/**
 * First incomplete screen in the wizard, or `null` if all wizard API steps are done
 * or onboarding is marked completed.
 */
export function getFirstIncompleteWizardDisplayStep(
  settings: iOnboardingFlagsAPI | string | null | undefined
): number | null {
  if (settings === undefined || settings === null) return 1;

  let parsed: Partial<iOnboardingFlagsAPI> & { onboarding_completed?: boolean };
  try {
    parsed =
      typeof settings === "string" ? JSON.parse(settings) : settings;
  } catch {
    return 1;
  }

  if (!parsed) return 1;
  if (parsed.onboarding_completed) return null;

  const allEmpty =
    !parsed.onboarding_step_1 &&
    !parsed.onboarding_step_2 &&
    (!parsed.onboarding_step_3 || parsed.onboarding_step_3 === 0) &&
    !parsed.onboarding_step_4 &&
    !parsed.onboarding_step_5 &&
    !parsed.onboarding_step_6;
    // !parsed.onboarding_step_7; // test email step no longer part of wizard

  if (allEmpty) return 1;

  for (let i = 0; i < ONBOARDING_WIZARD_API_SEQUENCE.length; i++) {
    const apiStep = ONBOARDING_WIZARD_API_SEQUENCE[i];
    if (!isOnboardingApiStepComplete(parsed, apiStep)) {
      return i + 1;
    }
  }

  return null;
}
